import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { renderAsync as renderDocx } from "docx-preview";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import { getFileKind } from "../lib/fileKind";
import { downloadResource } from "../lib/download";
import { logError } from "../lib/log";
import { usePdfRenderer, ZOOM_STEPS } from "../hooks/usePdfRenderer";
import { X, Eye, Loader2, Download, Printer, ZoomIn, ZoomOut, Maximize2, FileWarning } from "lucide-react";

const DARK_BG = { backgroundColor: "#374151" };

function CenterMessage({ children }) {
  return <div className="w-full h-full flex items-center justify-center text-white/80" style={DARK_BG}>{children}</div>;
}

function CenterSpinner({ note }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-3" style={DARK_BG}>
      <Loader2 size={34} className="animate-spin" style={{ color: "#F97316" }} />
      {note && <p className="text-white/70 text-sm">{note}</p>}
    </div>
  );
}

function ZoomBar({ currentPage, numPages, zoomIdx, onOut, onIn, onReset }) {
  return (
    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full px-2 py-1.5 shadow-xl"
      style={{ backgroundColor: "rgba(10,31,68,0.95)", border: "1px solid #1E3A8A" }} data-testid="pdf-controls">
      <span data-testid="pdf-page-indicator" className="text-white text-sm font-medium px-3 whitespace-nowrap">
        Page {currentPage} / {numPages || "…"}
      </span>
      <div className="w-px h-5 bg-[#1E3A8A]" />
      <button data-testid="pdf-zoom-out" onClick={onOut} title="Zoom out"
        className="text-white p-1.5 rounded-full hover:bg-white/10 disabled:opacity-40" disabled={zoomIdx === 0}>
        <ZoomOut size={17} />
      </button>
      <button data-testid="pdf-zoom-reset" onClick={onReset} title="Fit width"
        className="text-white text-xs font-semibold px-2 rounded-full hover:bg-white/10 min-w-[48px]">
        {Math.round(ZOOM_STEPS[zoomIdx] * 100)}%
      </button>
      <button data-testid="pdf-zoom-in" onClick={onIn} title="Zoom in"
        className="text-white p-1.5 rounded-full hover:bg-white/10 disabled:opacity-40" disabled={zoomIdx === ZOOM_STEPS.length - 1}>
        <ZoomIn size={17} />
      </button>
      <button onClick={onReset} title="Reset" className="text-white p-1.5 rounded-full hover:bg-white/10">
        <Maximize2 size={15} />
      </button>
    </div>
  );
}

function PdfCanvas({ data }) {
  const { containerRef, status, numPages, zoomIdx, setZoomIdx } = usePdfRenderer(data);
  const [currentPage, setCurrentPage] = useState(1);

  const onScroll = () => {
    const container = containerRef.current;
    if (!container) return;
    const mid = container.scrollTop + container.clientHeight / 2;
    let pg = 1;
    container.querySelectorAll("canvas").forEach((c) => { if (c.offsetTop <= mid) pg = Number(c.dataset.page); });
    setCurrentPage(pg);
  };

  return (
    <div className="relative w-full h-full" style={DARK_BG}>
      <div ref={containerRef} onScroll={onScroll} className="w-full h-full overflow-auto py-6 px-2">
        {status === "error" && (
          <div className="flex items-center justify-center h-full text-white/80">Unable to display this document.</div>
        )}
        {/* canvases injected here */}
      </div>

      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 size={32} className="animate-spin" style={{ color: "#F97316" }} />
        </div>
      )}

      {status !== "error" && (
        <ZoomBar
          currentPage={currentPage}
          numPages={numPages}
          zoomIdx={zoomIdx}
          onOut={() => setZoomIdx((i) => Math.max(0, i - 1))}
          onIn={() => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
          onReset={() => setZoomIdx(3)}
        />
      )}
    </div>
  );
}

// ---------------- Word (.docx) — client-side render ----------------
function DocxView({ blob }) {
  const ref = useRef(null);
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!ref.current) return;
        ref.current.replaceChildren();
        await renderDocx(blob, ref.current, undefined, {
          className: "docx", inWrapper: true, ignoreWidth: false,
          ignoreHeight: false, breakPages: true, experimental: true,
        });
        if (!cancelled) setStatus("done");
      } catch (e) {
        logError("DOCX render failed:", e);
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [blob]);

  return (
    <div data-testid="docx-view" className="relative w-full h-full overflow-auto" style={DARK_BG}>
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 size={32} className="animate-spin" style={{ color: "#F97316" }} />
        </div>
      )}
      {status === "error" && (
        <div className="flex items-center justify-center h-full text-white/80">Unable to display this document.</div>
      )}
      <div ref={ref} className="py-6" />
    </div>
  );
}

// ---------------- Excel (.xlsx/.xls/.csv) — client-side render ----------------
const XLSX_STYLE = `
.xlsx-host table { border-collapse: collapse; width: max-content; background:#fff; }
.xlsx-host td, .xlsx-host th { border: 1px solid #E5E7EB; padding: 5px 10px; font-size: 13px; color:#1A1A1A; white-space: nowrap; }
.xlsx-host tr:first-child td { background:#F9FAFB; font-weight:600; }
`;

function XlsxView({ data }) {
  const [sheets, setSheets] = useState(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState(false);
  useEffect(() => {
    try {
      const wb = XLSX.read(data, { type: "array" });
      const s = wb.SheetNames.map((n) => ({ name: n, html: DOMPurify.sanitize(XLSX.utils.sheet_to_html(wb.Sheets[n])) }));
      setSheets(s);
    } catch (e) {
      logError("XLSX parse failed:", e);
      setError(true);
    }
  }, [data]);

  if (error) return <CenterMessage>Unable to display this spreadsheet.</CenterMessage>;
  if (!sheets) return <CenterSpinner />;

  return (
    <div data-testid="xlsx-view" className="w-full h-full flex flex-col" style={DARK_BG}>
      <style>{XLSX_STYLE}</style>
      {sheets.length > 1 && (
        <div className="flex gap-1 px-3 pt-3 flex-wrap">
          {sheets.map((s, i) => (
            <button key={s.name} data-testid={`xlsx-sheet-${i}`} onClick={() => setActive(i)}
              className="text-xs font-medium px-3 py-1.5 rounded-t-lg transition-colors"
              style={{ backgroundColor: i === active ? "#fff" : "rgba(255,255,255,0.12)", color: i === active ? "#0A1F44" : "#fff" }}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-auto p-4">
        <div className="xlsx-host bg-white rounded-lg p-3 inline-block" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sheets[active].html) }} />
      </div>
    </div>
  );
}

// ---------------- PowerPoint / legacy formats — not previewable inline ----------------
function OfficeNotice() {
  return (
    <div data-testid="office-notice" className="w-full h-full flex items-center justify-center p-6" style={DARK_BG}>
      <div className="max-w-md text-center bg-white rounded-2xl p-8 border" style={{ borderColor: "#E5E7EB" }}>
        <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#FEF0E6" }}>
          <FileWarning size={26} style={{ color: "#F97316" }} />
        </div>
        <h3 className="font-heading text-lg font-bold mb-2">Preview not available</h3>
        <p className="text-sm text-[#6B7280] leading-relaxed">
          This PowerPoint file can't be previewed inline. For the best view-only quality, please re-upload it as a{" "}
          <span className="font-semibold text-[#1A1A1A]">PDF</span> instead.
        </p>
      </div>
    </div>
  );
}

function ImageView({ src, alt }) {
  return (
    <div className="w-full h-full flex items-center justify-center overflow-auto p-4" style={DARK_BG}>
      <img src={src} alt={alt} className="max-w-full max-h-full object-contain select-none" draggable={false} />
    </div>
  );
}

// ---- Data loading ----
function useDocumentData(file, kind) {
  const [state, setState] = useState({ objUrl: null, pdfData: null, docxBlob: null, sheetData: null, loading: true, error: false });
  useEffect(() => {
    let url;
    let cancelled = false;
    const set = (patch) => { if (!cancelled) setState((s) => ({ ...s, ...patch })); };
    const run = async () => {
      try {
        if (kind.isPdf || kind.isImage) {
          const { data } = await api.get(`/files/${file.file_id}`, { responseType: "blob" });
          if (cancelled) return;
          url = URL.createObjectURL(data);
          set({ objUrl: url, pdfData: kind.isPdf ? await data.arrayBuffer() : null });
        } else if (kind.isDocx) {
          const { data } = await api.get(`/files/${file.file_id}`, { responseType: "blob" });
          set({ docxBlob: data });
        } else if (kind.isXlsx) {
          const { data } = await api.get(`/files/${file.file_id}`, { responseType: "arraybuffer" });
          set({ sheetData: data });
        } else if (!kind.isUnsupportedOffice) {
          // HTML + any other previewable type: fetch as an authenticated blob so the
          // iframe / print paths never hit the (auth-protected) /files URL without a token.
          const { data } = await api.get(`/files/${file.file_id}`, { responseType: "blob" });
          if (cancelled) return;
          url = URL.createObjectURL(data);
          set({ objUrl: url });
        }
      } catch (e) {
        logError("Document load failed:", e);
        set({ error: true });
      }
      set({ loading: false });
    };
    run();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);
  return state;
}

// ---- Layout ----
function ViewerHeader({ title, filename, allowDownload, canPrint, onDownload, onPrint, onClose }) {
  return (
    <div className="absolute top-0 left-0 right-0 h-14 flex items-center justify-between px-5 border-b z-10"
      style={{ borderColor: "#1E3A8A", backgroundColor: "#0A1F44" }}>
      <div className="flex items-center gap-2 text-white min-w-0">
        <Eye size={18} style={{ color: "#F97316" }} />
        <span className="font-heading font-semibold truncate">{title || filename}</span>
        {!allowDownload && <span className="text-xs text-[#9CA3AF] hidden sm:inline">· View only</span>}
      </div>
      <div className="flex items-center gap-2">
        {allowDownload && (
          <>
            <button data-testid="doc-viewer-download" onClick={onDownload} title="Download"
              className="flex items-center gap-1.5 text-sm text-white rounded-lg px-3 py-1.5 transition-colors hover:bg-white/10">
              <Download size={16} /> <span className="hidden sm:inline">Download</span>
            </button>
            {canPrint && (
              <button data-testid="doc-viewer-print" onClick={onPrint} title="Print"
                className="flex items-center gap-1.5 text-sm text-white rounded-lg px-3 py-1.5 transition-colors hover:bg-white/10">
                <Printer size={16} /> <span className="hidden sm:inline">Print</span>
              </button>
            )}
          </>
        )}
        <button data-testid="doc-viewer-close" onClick={onClose} className="text-white rounded-lg p-2 transition-colors hover:bg-white/10">
          <X size={22} />
        </button>
      </div>
    </div>
  );
}

function HtmlFrame({ src }) {
  return (
    <iframe title="document" src={src}
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation allow-pointer-lock"
      allowFullScreen
      className="w-full h-full border-0 bg-white" />
  );
}

function pickViewer({ file, kind, data, title }) {
  if (kind.isUnsupportedOffice) return <OfficeNotice />;
  if (kind.isPdf && data.pdfData) return <PdfCanvas data={data.pdfData} />;
  if (kind.isDocx && data.docxBlob) return <DocxView blob={data.docxBlob} />;
  if (kind.isXlsx && data.sheetData) return <XlsxView data={data.sheetData} />;
  if (kind.isImage) return <ImageView src={data.objUrl} alt={title} />;
  if (kind.isHtml) return <HtmlFrame src={data.objUrl} />;
  return <iframe title="document" src={data.objUrl} className="w-full h-full border-0" />;
}

function ViewerBody(props) {
  const { kind, data } = props;
  if (data.loading) return <CenterSpinner note={(kind.isDocx || kind.isXlsx) ? "Preparing document…" : null} />;
  if (data.error) return <CenterMessage>Unable to display this document.</CenterMessage>;
  return pickViewer(props);
}

export function InlineDocView({ file, title }) {
  const kind = useMemo(() => getFileKind(file), [file]);
  const data = useDocumentData(file, kind);
  if (!file) {
    return <CenterMessage>No document file has been uploaded yet.</CenterMessage>;
  }
  return (
    <div className="w-full h-full bg-white" onContextMenu={(e) => e.preventDefault()}>
      <ViewerBody file={file} kind={kind} data={data} title={title} />
    </div>
  );
}

export default function DocViewer({ file, title, onClose, allowDownload = false }) {
  const kind = useMemo(() => getFileKind(file), [file]);
  const data = useDocumentData(file, kind);
  if (!file) return null;

  const doDownload = () => downloadResource(file.file_id, file.filename || "document");
  const doPrint = () => {
    if (!data.objUrl) return;
    const w = window.open(data.objUrl, "_blank");
    if (w) w.addEventListener("load", () => { try { w.print(); } catch (e) { logError("Print failed:", e); } });
  };

  return createPortal(
    <div data-testid="doc-viewer-overlay" className="fixed inset-0 z-[60]" style={{ backgroundColor: "#0A1F44" }}>
      <ViewerHeader title={title} filename={file.filename} allowDownload={allowDownload}
        canPrint={kind.canPrint} onDownload={doDownload} onPrint={doPrint} onClose={onClose} />
      <div className="absolute top-14 left-0 right-0 bottom-0 bg-white"
        onContextMenu={(e) => (allowDownload ? null : e.preventDefault())}>
        <ViewerBody file={file} kind={kind} data={data} title={title} />
      </div>
    </div>,
    document.body
  );
}
